import { useTranslation } from '../../../shared/i18n';
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    Card, Table, Tag, Button, Space, Modal, message, Empty, Tooltip,
    Typography, Row, Col, Statistic, Descriptions, Switch, Tabs, Popconfirm
} from 'antd';
import {
    RocketOutlined, GlobalOutlined, CloudOutlined, MailOutlined, AppstoreOutlined,
    EyeOutlined, SafetyCertificateOutlined, CloudServerOutlined, ContainerOutlined, LinkOutlined
} from '@ant-design/icons';
import billingService from '../../services/billingService';
import RenewAction from '../../../shared/components/RenewAction';
import AddonPanel from '../../../shared/components/AddonPanel';
import AutoRenewNudge from '../../components/AutoRenewNudge';
import { formatMoney } from '../../../shared/utils/money';
import moment from 'moment';

import { userPath } from '../../../shared/config/paths';

const { Text } = Typography;

const TYPE_ORDER = ['cdn', 'dns', 'yeapmail', 'evm', 'host', 'host_shared', 'ssl', 'domain', 'disk'];

// 资源页（详情）
const resourcePath = (code, res) => {
    if (code === 'disk' && res) return res.id ? `/disk/orgs/${res.id}` : '/disk'; // 个人云盘 id=0 → 个人空间
    if (!res || !res.id) return null;
    switch (code) {
        case 'cdn': return res.name ? `/cdn/domains/${encodeURIComponent(res.name)}` : null;
        case 'dns': return `/dns/records/${res.id}`;
        case 'yeapmail': return '/webmail/domains';
        case 'evm':
        case 'vm': return `/evm/vms/${res.id}`;
        case 'host_shared': return `/host_shared/vhosts/${res.id}`;
        case 'host': return `/containers/legacy/${res.id}`;
        case 'container': return `/containers/${res.id}`;
        case 'tm': return '/tm/streams';
        case 'domain': return `/domain-registration/my-domains/${res.id}`;
        default: return null;
    }
};

// 资源的套餐页（升级/降级在这里）；host_shared/host/lightapp 不支持在线换套餐
const planPath = (code, res) => {
    if (!res || !res.id) return null;
    switch (code) {
        case 'cdn': return `/cdn/plans?domain_id=${res.id}`;
        case 'dns': return `/dns/plans?zone_id=${res.id}`;
        case 'yeapmail': return `/webmail/plans?domain_id=${res.id}`;
        case 'evm':
        case 'vm': return `/evm/vms/${res.id}?tab=upgrade`;
        default: return null;
    }
};

const Subscription = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [subscriptions, setSubscriptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [detailModalVisible, setDetailModalVisible] = useState(false);
    const [selectedSubscription, setSelectedSubscription] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [togglingId, setTogglingId] = useState(null);
    const [typeCounts, setTypeCounts] = useState({});
    const [productType, setProductType] = useState('');
    const [includeFree, setIncludeFree] = useState(false);
    const [includeEnded, setIncludeEnded] = useState(false);
    const [autoOffOnly, setAutoOffOnly] = useState(false);
    const [paySource, setPaySource] = useState(null);
    const [enablingAll, setEnablingAll] = useState(false);
    const [nudgeKey, setNudgeKey] = useState(0);
    // 服务端分页（默认 20，最大 100）
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [total, setTotal] = useState(0);

    useEffect(() => {
        loadSubscriptions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, pageSize, productType, includeFree, includeEnded, autoOffOnly]);

    const loadSubscriptions = async () => {
        setLoading(true);
        try {
            const params = { page, page_size: pageSize };
            if (productType) params.product_type = productType;
            if (includeFree) params.include_free = 1;
            if (includeEnded) params.include_ended = 1;
            if (autoOffOnly) params.auto_renew = 0;
            const response = await billingService.getSubscriptions(params);
            if (response.success) {
                const list = Array.isArray(response.data) ? response.data : [];
                setSubscriptions(list);
                setTotal(response.pagination?.total ?? list.length);
                setPaySource(response.payment_source || null);
                const tc = response.type_counts;
                setTypeCounts(tc && typeof tc === 'object' && !Array.isArray(tc) ? tc : {});
            } else {
                message.error(response.error || t('Failed to load subscription list'));
            }
        } catch (error) {
            message.error(t('Failed to load subscription list: {{value}}', { value: error.error || error.message }));
        } finally {
            setLoading(false);
        }
    };

    const loadSubscriptionDetail = async (id) => {
        setDetailLoading(true);
        try {
            const response = await billingService.getSubscription(id);
            if (response.success) {
                setSelectedSubscription(response.data);
            } else {
                message.error(response.error || t('Failed to load subscription details'));
            }
        } catch (error) {
            message.error(t('Failed to load subscription details: {{value}}', { value: error.error || error.message }));
        } finally {
            setDetailLoading(false);
        }
    };

    const handleViewDetail = (record) => {
        loadSubscriptionDetail(record.id);
        setDetailModalVisible(true);
    };

    // ?id=123 直接打开该订阅详情（资源页的附加包入口）
    const [searchParams] = useSearchParams();
    const openId = Number(searchParams.get('id')) || 0;
    useEffect(() => {
        if (openId) handleViewDetail({ id: openId });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [openId]);

    // 0 元体验上打开自动续费：先确认体验结束后的续费价格
    const cycleUnit = (cycle) => ({
        monthly: t('month'), quarterly: t('quarter'), semi_annually: t('6 months'), annually: t('year'),
        yearly: t('year'), biennially: t('2 years'), triennially: t('3 years'),
    }[cycle] || cycle);
    const confirmToggleAutoRenew = (record) => {
        if (record.auto_renew || !record.on_trial) return handleToggleAutoRenew(record);
        const r = record.renewal;
        const price = r && r.amount != null ? formatMoney(r.amount, r.currency) : '-';
        Modal.confirm({
            title: t('Turn on auto-renewal?'),
            content: t('Renews at {{price}}/{{cycle}} when the trial ends', { price, cycle: cycleUnit(record.billing_cycle) }),
            okText: t('Enable'),
            cancelText: t('Cancel'),
            onOk: () => handleToggleAutoRenew(record),
        });
    };

    // 关闭自动续费 = 到期不续费，服务到期结束
    const handleToggleAutoRenew = async (record) => {
        setTogglingId(record.id);
        try {
            const response = await billingService.toggleAutoRenew(record.id, !record.auto_renew);
            if (response.success) {
                message.success(response.message || t('Auto-renewal status updated'));
                setSubscriptions((prev) => prev.map((s) =>
                    s.id === record.id
                        ? { ...s, auto_renew: response.auto_renew, billing: s.billing ? { ...s.billing, auto_renew: response.auto_renew } : s.billing }
                        : s
                ));
                setNudgeKey((k) => k + 1);
            } else {
                message.error(response.error || t('Operation failed'));
            }
        } catch (error) {
            message.error(t('Operation failed: {{value}}', { value: error.error || error.message }));
        } finally {
            setTogglingId(null);
        }
    };

    // 一键打开全部（只动自己的、可续费的、关着的）
    const handleEnableAll = async () => {
        setEnablingAll(true);
        try {
            const r = await billingService.enableAutoRenewAll();
            if (r.success) {
                message.success(r.message || t('Auto-renewal enabled'));
                setNudgeKey((k) => k + 1);
                loadSubscriptions();
            } else {
                message.error(r.error || t('Operation failed'));
            }
        } catch (error) {
            message.error(t('Operation failed: {{value}}', { value: error.error || error.message }));
        } finally {
            setEnablingAll(false);
        }
    };

    // 下次续费：日期 · 金额 · 扣款来源（余额 / 卡 / 余额不足）
    const renderNextRenewal = (record) => {
        const r = record.renewal;
        if (!r) return <Text type="secondary">-</Text>;
        const src = !record.auto_renew ? null : r.source === 'balance'
            ? <Tag color="green" style={{ margin: 0 }}>{t('Balance')}</Tag>
            : r.source === 'card'
                ? <Tag color="blue" style={{ margin: 0 }}>{paySource?.card?.last4 ? `•••• ${paySource.card.last4}` : t('Card')}</Tag>
                : <Tooltip title={t('Top up your balance before the renewal date')}><Tag color="orange" style={{ margin: 0 }}>{t('Insufficient balance')}</Tag></Tooltip>;
        return (
            <Space size={4} wrap>
                <span>{r.date ? moment(r.date).format('YYYY-MM-DD') : '-'}</span>
                {r.amount != null && <Text type="secondary">{formatMoney(r.amount, r.currency)}</Text>}
                {src}
            </Space>
        );
    };

    const getStatusTag = (status) => {
        const statusMap = {
            'active': { color: 'success', text: t('Active') },
            'pending': { color: 'processing', text: t('Activation pending') },
            'suspended': { color: 'warning', text: t('Paused') },
            'cancelled': { color: 'default', text: t('Canceled') },
            'expired': { color: 'error', text: t('Expired') },
            'terminated': { color: 'error', text: t('Terminated') },
            'failed': { color: 'error', text: t('Failed') }
        };
        const config = statusMap[status] || { color: 'default', text: status };
        return <Tag color={config.color}>{config.text}</Tag>;
    };

    const getProductTypeIcon = (type) => {
        const iconMap = {
            'dns': <GlobalOutlined />,
            'cdn': <CloudOutlined />,
            'yeapmail': <MailOutlined />,
            'host_shared': <AppstoreOutlined />,
            'host': <ContainerOutlined />,
            'evm': <CloudServerOutlined />,
            'vm': <CloudServerOutlined />,
            'ssl': <SafetyCertificateOutlined />,
            'domain': <GlobalOutlined />,
            'disk': <CloudOutlined />
        };
        return iconMap[type] || <AppstoreOutlined />;
    };

    const getProductTypeLabel = (type) => {
        const labelMap = {
            'dns': 'DNS',
            'cdn': 'CDN',
            'yeapmail': t('Business email'),
            'host_shared': t('Web hosting'),
            'host': t('Dedicated Container (legacy)'),
            'container': t('Dedicated Containers'),
            'ssl': t('SSL certificates'),
            'domain': t('Domain registration'),
            'evm': t('Cloud servers'),
            'vm': t('Cloud servers'),
            'tm': t('Email sending'),
            'disk': t('Cloud Drive'),
            'lightapp': t('LightApps')
        };
        return labelMap[type] || type;
    };

    // 周期用量：内部 feature_key → 名称；*_bytes 按字节换算
    const usageLabel = (key) => ({
        traffic_bytes: t('Traffic'), disk_traffic_bytes: t('Direct-link traffic'), cdn_traffic_bytes: t('Traffic'),
        requests: t('Requests'), dns_queries: t('DNS queries'), emails: t('Email'), bandwidth: t('Bandwidth'),
    }[key] || key);
    const usageValue = (key, v) => {
        if (!String(key).endsWith('_bytes')) return Number(v || 0).toLocaleString();
        let n = Number(v || 0);
        const units = ['B', 'KB', 'MB', 'GB', 'TB'];
        let i = 0;
        while (n >= 1024 && i < units.length - 1) { n /= 1024; i++; }
        return `${n.toFixed(i ? 2 : 0)} ${units[i]}`;
    };

    // 云盘套餐规格（plan_features）：键 → 名称 / 值
    const diskFeatureLabel = (key) => ({
        disk_mode: t('Type'), disk_storage_gb: t('Storage'), disk_seats: t('Members'),
        disk_seats_5g: t('5GB seats'), disk_seats_10g: t('10GB seats'), disk_traffic_bytes: t('Direct-link traffic'),
    }[key] || key);
    const diskFeatureValue = (key, v) => {
        if (key === 'disk_mode') return ({ personal: t('Personal'), org_pool: t('Organization (shared)'), org_seat: t('Organization (per seat)') }[v] || v);
        if (key === 'disk_storage_gb') return Number(v) >= 1024 ? `${+(Number(v) / 1024).toFixed(2)} TB` : `${Number(v) || 0} GB`;
        if (key === 'disk_traffic_bytes') return usageValue(key, v);
        return Number(v || 0).toLocaleString();
    };

    const cycleLabel = (cycle) => {
        const cycleMap = {
            'monthly': t('Monthly billing'),
            'quarterly': t('Quarterly'),
            'semi_annually': t('Every 6 months'),
            'annually': t('Yearly'),
            'yearly': t('Yearly'),
            'biennially': t('Every 2 years'),
            'triennially': t('Every 3 years'),
            'onetime': t('One-time')
        };
        return cycleMap[cycle] || cycle || '-';
    };

    const renderResource = (resource, record) => {
        if (!resource) return <Text type="secondary">-</Text>;
        const path = resourcePath(record.product_type_code, resource);
        const extra = Number(resource.extra_count) || 0;
        const label = record.product_type_code === 'disk' && !resource.id ? t('Personal space') : resource.name;
        const name = path
            ? <Button type="link" size="small" style={{ padding: 0 }} onClick={() => navigate(userPath(path))}>{label}</Button>
            : <Text>{label}</Text>;
        return (
            <Space size={4}>
                {name}
                {extra > 0 && (
                    <Tooltip title={(resource.members || []).join(', ')}>
                        <Tag style={{ margin: 0 }}>+{extra}</Tag>
                    </Tooltip>
                )}
            </Space>
        );
    };

    const columns = [
        {
            title: t('Product type'),
            dataIndex: 'product_type_code',
            key: 'product_type_code',
            width: 110,
            render: (type) => (
                <Space>
                    {getProductTypeIcon(type)}
                    <span>{getProductTypeLabel(type)}</span>
                </Space>
            )
        },
        {
            title: t('Plan name'),
            dataIndex: 'product_name',
            key: 'product_name',
            width: 180,
            render: (name, record) => (
                <Space size={4} wrap>
                    <span>{name}</span>
                    {(record.addons || []).filter(Boolean).map((a, i) => (
                        <Tag key={i} color="blue" style={{ margin: 0 }}>{a}</Tag>
                    ))}
                </Space>
            )
        },
        {
            title: t('Linked resources'),
            dataIndex: 'resource',
            key: 'resource',
            render: renderResource
        },
        {
            title: t('Status'),
            dataIndex: 'status',
            key: 'status',
            width: 90,
            render: (status) => getStatusTag(status)
        },
        {
            title: t('Billing cycle'),
            dataIndex: 'billing_cycle',
            key: 'billing_cycle',
            width: 90,
            render: cycleLabel
        },
        {
            title: t('Expires'),
            key: 'expiry',
            width: 200,
            render: (_, record) => (
                <RenewAction billing={record.billing} resourceName={record.resource?.name} onRenewed={loadSubscriptions} />
            )
        },
        {
            title: (
                <Tooltip title={t('When off, the service is not renewed and ends at expiry')}>
                    <span>{t('Auto-renewal')}</span>
                </Tooltip>
            ),
            dataIndex: 'auto_renew',
            key: 'auto_renew',
            width: 90,
            render: (auto, record) => (
                <Tooltip title={record.auto_renew_note || null}>
                    <Switch
                        size="small"
                        checked={!!auto}
                        loading={togglingId === record.id}
                        disabled={!!record.auto_renew_note}
                        onChange={() => confirmToggleAutoRenew(record)}
                    />
                </Tooltip>
            )
        },
        {
            title: t('Next renewal'),
            key: 'renewal',
            width: 210,
            render: (_, record) => renderNextRenewal(record)
        },
        {
            title: t('Actions'),
            key: 'actions',
            width: 170,
            render: (_, record) => {
                const up = ['active', 'suspended'].includes(record.status) ? planPath(record.product_type_code, record.resource) : null;
                return (
                    <Space>
                        <Button size="small" icon={<EyeOutlined />} onClick={() => handleViewDetail(record)}>
                            {t('Details')}
                        </Button>
                        {up && (
                            <Button size="small" type="primary" icon={<RocketOutlined />} onClick={() => navigate(userPath(up))}>
                                {t('Upgrade')}
                            </Button>
                        )}
                    </Space>
                );
            }
        }
    ];

    const renderDetailModal = () => {
        if (!selectedSubscription) return null;
        const sub = selectedSubscription;
        const row = subscriptions.find((s) => s.id === sub.id);
        const plan = planPath(sub.product_type_code, row?.resource);
        const items = sub.resources?.items || [];

        return (
            <Modal
                title={t('Subscription details')}
                open={detailModalVisible}
                onCancel={() => {
                    setDetailModalVisible(false);
                    setSelectedSubscription(null);
                }}
                footer={plan ? (
                    <Button type="primary" icon={<LinkOutlined />} onClick={() => navigate(userPath(plan))}>{t('View plans')}</Button>
                ) : null}
                width={760}
                loading={detailLoading}
            >
                <Descriptions bordered column={2} style={{ marginBottom: 24 }}>
                    <Descriptions.Item label={t('Plan name')}>{sub.product_name}</Descriptions.Item>
                    <Descriptions.Item label={t('Status')}>{getStatusTag(sub.status)}</Descriptions.Item>
                    <Descriptions.Item label={t('Billing cycle')}>{cycleLabel(sub.billing_cycle)}</Descriptions.Item>
                    <Descriptions.Item label={t('Auto-renewal')}>
                        {sub.billing?.gift
                            ? <Tag color="purple">{sub.billing.gift.source === 'product' ? t('Gift · follows mail plan') : t('Gift · follows domain')}</Tag>
                            : (sub.auto_renew ? <Tag color="green">{t('Enable')}</Tag> : <Tag>{t('Close')}</Tag>)}
                    </Descriptions.Item>
                    <Descriptions.Item label={t('Start time')}>
                        {sub.activation_date ? moment(sub.activation_date).format('YYYY-MM-DD') : '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label={t('Expires at')}>
                        {sub.expiry_date ? moment(sub.expiry_date).format('YYYY-MM-DD') : '-'}
                    </Descriptions.Item>
                </Descriptions>

                {sub.plan_features && Object.keys(sub.plan_features).length > 0 && (
                    <Descriptions size="small" bordered column={2} title={t('Plan specs')} style={{ marginBottom: 16 }}>
                        {Object.entries(sub.plan_features).map(([k, v]) => (
                            <Descriptions.Item key={k} label={diskFeatureLabel(k)}>{diskFeatureValue(k, v)}</Descriptions.Item>
                        ))}
                    </Descriptions>
                )}

                {items.length > 0 && (
                    <Card title={t('Linked resources')} size="small" style={{ marginBottom: 16 }}>
                        <Table
                            dataSource={items}
                            rowKey="id"
                            size="small"
                            pagination={false}
                            columns={[
                                {
                                    title: t('Name'),
                                    key: 'name',
                                    render: (_, r) => r.zone || r.domain || r.domain_name || r.name || r.hostname || r.id
                                },
                                {
                                    title: t('Status'),
                                    dataIndex: 'status',
                                    key: 'status',
                                    render: (s) => <Tag color={s === 'active' ? 'success' : 'default'}>{s}</Tag>
                                },
                                {
                                    title: t('Created at'),
                                    dataIndex: 'created_at',
                                    key: 'created_at',
                                    render: (d) => (d ? moment(d).format('YYYY-MM-DD') : '-')
                                }
                            ]}
                        />
                    </Card>
                )}

                {!sub.is_free && <AddonPanel key={sub.id} subscriptionId={sub.id} />}

                {sub.usage && sub.usage.length > 0 && (
                    <Card title={t('Usage this period')} size="small">
                        <Row gutter={[16, 16]}>
                            {sub.usage.map((usage) => (
                                <Col xs={24} sm={12} key={usage.feature_key}>
                                    <Statistic
                                        title={usageLabel(usage.feature_key)}
                                        value={usageValue(usage.feature_key, usage.used_value ?? usage.current_usage)}
                                        suffix={usage.reset_cycle === 'monthly' ? t('/ month') : ''}
                                    />
                                </Col>
                            ))}
                        </Row>
                    </Card>
                )}
            </Modal>
        );
    };

    const tabKeys = [
        ...TYPE_ORDER.filter((k) => typeCounts[k]),
        ...Object.keys(typeCounts).filter((k) => !TYPE_ORDER.includes(k) && typeCounts[k]),
    ];
    const allCount = Object.values(typeCounts).reduce((a, b) => a + (Number(b) || 0), 0);
    const tabItems = [
        { key: '', label: `${t('All')} ${allCount || ''}`.trim() },
        ...tabKeys.map((k) => ({ key: k, label: `${getProductTypeLabel(k)} ${typeCounts[k]}` })),
    ];
    const resetPage = (fn) => (v) => { setPage(1); fn(v); };

    return (
        <div>
            <Card
                title={t('Subscriptions')}
                bordered={false}
                extra={
                    <Space size={16} wrap>
                        <Space size={6}>
                            <Switch size="small" checked={includeFree} onChange={resetPage(setIncludeFree)} />
                            <span>{t('Show free plans')}</span>
                        </Space>
                        <Space size={6}>
                            <Switch size="small" checked={includeEnded} onChange={resetPage(setIncludeEnded)} />
                            <span>{t('Show ended')}</span>
                        </Space>
                        <Space size={6}>
                            <Switch size="small" checked={autoOffOnly} onChange={resetPage(setAutoOffOnly)} />
                            <span>{t('Auto-renewal off')}</span>
                        </Space>
                        <Popconfirm title={t('Turn on auto-renewal for all?')} onConfirm={handleEnableAll}>
                            <Button loading={enablingAll}>{t('Turn on auto-renewal for all')}</Button>
                        </Popconfirm>
                        <Button onClick={loadSubscriptions} loading={loading}>{t('Refresh')}</Button>
                    </Space>
                }
            >
                <AutoRenewNudge key={nudgeKey} onEnabled={loadSubscriptions} />
                <Tabs activeKey={productType} onChange={resetPage(setProductType)} items={tabItems} />
                {subscriptions.length === 0 && !loading && total === 0 ? (
                    <Empty description={t('No subscriptions yet')} image={Empty.PRESENTED_IMAGE_SIMPLE}>
                        <Space>
                            <Button type="primary" onClick={() => navigate(userPath('/cdn/domains'))}>{t('CDN domains')}</Button>
                            <Button onClick={() => navigate(userPath('/dns/zones'))}>{t('DNS zones')}</Button>
                        </Space>
                    </Empty>
                ) : (
                    <Table
                        columns={columns}
                        dataSource={subscriptions}
                        rowKey="id"
                        loading={loading}
                        scroll={{ x: 1200 }}
                        pagination={{
                            current: page,
                            pageSize,
                            total,
                            showSizeChanger: true,
                            pageSizeOptions: ['20', '50', '100'],
                            showTotal: (total) => t('{{total}} subscriptions total', { total }),
                            onChange: (p, ps) => {
                                if (ps !== pageSize) { setPageSize(ps); setPage(1); } else { setPage(p); }
                            },
                        }}
                    />
                )}
            </Card>

            {renderDetailModal()}
        </div>
    );
};

export default Subscription;
